import dayjs from 'dayjs';
import React from 'react';
import { ComposedChart, Bar, XAxis, YAxis, Line, Tooltip, TooltipProps, ResponsiveContainer, Cell } from 'recharts';
import styled from 'styled-components';
import { colors } from "../../utils/colors";
import * as TypographyStyles from '../TypographyStyles';
import { TransferHistory } from './Types/transferHistory';
import { slateColors, purpleColors } from '../ContentPages/AppLandingPage/styles';
import { useTranslation } from 'react-i18next';

const ContainerCard = styled('div')`
    display: flex;
    height: 270px;
    padding: 1.5rem;
    flex-direction: column;
    align-items: flex-start;
    gap: 1.25rem;
    align-self: stretch;
    border-radius: 2.5rem;
    border: 1px solid ${slateColors[200]};
    background: white;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1);
    box-sizing: border-box;
    transition: all 0.3s;
    
    &:hover {
        box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
        border-color: ${slateColors[300]};
    }
`

const CardHeader = styled('div')`
    display: flex;
    align-items: flex-start;
    gap: 4px;
`

const ChartContainer = styled('div')`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    flex: 1 0 0;
    align-self: stretch;
`

const LegendContainer = styled('div')`
    display: flex;
    align-items: end;
    gap: 8px;
`

const KeyDot = styled('div')`
    width: 12px;
    height: 12px;
    background: linear-gradient(180deg, ${purpleColors[500]} 0%, ${purpleColors[600]} 100%);
    border-radius: 50%;
`;

// Define styled component for tooltip
const CustomTooltipContainer = styled.div`
  background-color: white;
  border: 1px solid ${slateColors[200]};
  border-radius: 0.75rem;
  padding: 0.75rem;
  font-size: 0.875rem;
  color: ${slateColors[800]};
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
`;

const CustomTooltip: React.FC<TooltipProps<any, any>> = ({ active, payload, label }) => {
    const { t } = useTranslation();
    
    if (active && payload && payload.length) {
      const { transferTimeMinutes, movingAverage } = payload[0].payload;
  
      return (
        <CustomTooltipContainer>
          <div><TypographyStyles.BodyMSemibold>{t('statistics.chartLabels.date')}</TypographyStyles.BodyMSemibold> {label}</div>
          <div><TypographyStyles.BodyMSemibold>{t('statistics.chartLabels.transferTime')}</TypographyStyles.BodyMSemibold> {transferTimeMinutes != null ? `${Math.round(transferTimeMinutes)} ${t('statistics.chartLabels.min')}` : 'N/A'}</div>
          <div><TypographyStyles.BodyMSemibold>{t('statistics.chartLabels.movingAverage')}</TypographyStyles.BodyMSemibold> {movingAverage != null ? `${Math.round(movingAverage)} ${t('statistics.chartLabels.min')}` : 'N/A'}</div>
        </CustomTooltipContainer>
      );
    }
  
    return null;
  };


interface TransferTimeEvolutionCardProps {
    transferHistoryData: TransferHistory[];
}

interface DataPoint {
    date: string;
    transferTimeMinutes: number | null; // Allow null values
    [key: string]: any; // For any additional properties in the data points
}

const TransferTimeEvolutionCard: React.FC<TransferTimeEvolutionCardProps> = ({ transferHistoryData }) => {
    const { t } = useTranslation();
    
    const today = dayjs();
    const days = Array.from({ length: 30 }, (_, i) => today.subtract(i, 'day').format('YYYY-MM-DD')).reverse();

    const data = days.map(day => {
        // Filter entries for the current day
        const relevantEntries = transferHistoryData.filter(entry => {
            const dateField = entry.plannedArrival;
            return dayjs(dateField).format('YYYY-MM-DD') === day;
        });
        
        // Calculate total transfer time, ignoring null values
        const totalTransferSeconds = relevantEntries.reduce((acc, entry) => {
            const transferTime = entry.secondsToChange;
            if (transferTime != null) {
                return acc + transferTime;
            }
            return acc; // Skip adding if transferTime is null
        }, 0);
    
        // If there are no valid transferTimes, set transferTime to null
        const finalTransferSeconds = relevantEntries.some(entry => {
            const transferTime = entry.secondsToChange;
            return transferTime != null;
        }) ? totalTransferSeconds : null;
        
        return { date: dayjs(day).format('D/M'), transferTimeMinutes: finalTransferSeconds === null ? null : finalTransferSeconds / 60 };
    });

    const calculateMovingAverage = (data: DataPoint[], windowSize: number): DataPoint[] => {
        return data.map((item, index, array) => {
            // Include only non-null transferTimes in the moving average window
            const window = array.slice(Math.max(0, index - windowSize + 1), index + 1)
                .filter(point => point.transferTimeMinutes != null);
    
    
            const sum = window.reduce((total, currentItem) => total + (currentItem.transferTimeMinutes || 0), 0);
            const average = window.length > 0 ? sum / window.length : null; // Handle empty window
        
            return {
                ...item,
                movingAverage: average,
            };
        });
    };

    // Function to determine bar color based on transferTime
    const getBarColor = (transferTime: number | null) => {
        if (transferTime != null && transferTime <= 0) return colors.red;
        return colors.grey400;
    };
    
    const movingAverageData = calculateMovingAverage(data, 14);

    return (
        <ContainerCard>
            <CardHeader>
                <TypographyStyles.BodyLSemibold color={slateColors[900]}>
                    {t('statistics.transferTimeEvolution')}
                </TypographyStyles.BodyLSemibold>
                <TypographyStyles.BodyLRegular color={slateColors[600]}>
                    {t('statistics.last30Days')}
                </TypographyStyles.BodyLRegular>
            </CardHeader>
            <ChartContainer>
                <TypographyStyles.BodySRegular color={slateColors[600]}>{t('statistics.chartLabels.min')}</TypographyStyles.BodySRegular>
                <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={movingAverageData} margin={{ top: 0, right: 0, left: -30, bottom: -10 }}>
                        <XAxis 
                            dataKey="date" 
                            tick={{ 
                                fontSize: 12, 
                                fill: slateColors[800],
                            }} 
                            axisLine={false}
                            tickLine={false}
                        />
                        <YAxis 
                            tick={{ 
                                fontSize: 12, 
                                fill: slateColors[600], 
                            }} 
                            axisLine={false}
                            tickLine={false}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar 
                            dataKey="transferTimeMinutes"
                            radius={[999, 999, 999, 999]} 
                        >
                            {movingAverageData.map((entry, index) => (
                                <Cell 
                                    key={`cell-${index}`} 
                                    fill={getBarColor(entry.transferTimeMinutes)}
                                />
                            ))}
                        </Bar>
                        {movingAverageData.some(item => item.movingAverage !== undefined) && (
                            <Line 
                                type="monotone" 
                                dataKey="movingAverage" 
                                stroke={colors.key} 
                                dot={false} 
                                strokeWidth={2}
                                strokeLinecap="round" // Adds rounded ends to the line
                            />
                        )}
                    </ComposedChart>
                </ResponsiveContainer>
                <LegendContainer>
                    <KeyDot></KeyDot>
                    <TypographyStyles.BodySRegular color={slateColors[800]}>{t('statistics.chartLabels.rollingAverage14d')}</TypographyStyles.BodySRegular>
                </LegendContainer>
            </ChartContainer>
        </ContainerCard>
    );
};

export default TransferTimeEvolutionCard
